/**
 * Growth Systems registry — Refinity's three productized engagements, sold as
 * "Practical Growth Systems".
 *
 * Every link to a growth system goes through `growthSystemPath()` rather than a
 * string literal. Two of the site's broken links were hand-typed hrefs to pages
 * that never existed; a typed id turns that mistake into a compile error, and
 * keeps the URL base in one constant.
 *
 * Pure data: no React or Next imports, so `scripts/verify-registry.ts` can load
 * it under tsx.
 *
 * Growth systems are packaged engagements (what you buy). They have no child
 * pages — they link DOWN to the service disciplines in `lib/service-topics.ts`.
 * See docs/page-intent-map.md.
 */

/**
 * URL base for the growth-system pages and their hub. These lived at /offers
 * until 2026-09; next.config.ts 301s every old URL here.
 */
export const GROWTH_SYSTEMS_BASE = '/growth-systems'

/** The hub listing all three systems. */
export const GROWTH_SYSTEMS_HUB = GROWTH_SYSTEMS_BASE

export type GrowthSystemId = 'marketing-automation' | 'brand-awareness' | 'lead-generation'

export type GrowthSystem = {
  /** Stable internal key. Never changes, whatever the URL does. */
  id: GrowthSystemId
  /** URL segment under `GROWTH_SYSTEMS_BASE`. */
  slug: string
  name: string
  /** The one query this page owns. See docs/page-intent-map.md. */
  targetQuery: string
  /** One-line promise, used on cards. */
  headline: string
  /** Card body copy. */
  summary: string
  /** White-on-black illustration; rendered with `mono-invert`. */
  image: string
  imageAlt: string
}

export const GROWTH_SYSTEMS: readonly GrowthSystem[] = [
  {
    id: 'marketing-automation',
    slug: 'marketing-automation',
    name: 'Marketing Automation',
    targetQuery: 'marketing automation services',
    headline: 'Turn manual marketing work into systems that run on their own.',
    summary:
      'Your SOPs, built into a native or integrated stack — n8n, Zapier and your CRM — so execution and reporting stop depending on someone remembering.',
    image: '/images/offers/marketing-automation-dark-mode.png',
    imageAlt: 'AI automation workflow illustration',
  },
  {
    id: 'brand-awareness',
    slug: 'brand-awareness',
    name: 'Brand Awareness',
    targetQuery: 'brand awareness services',
    headline: 'Be the business your market sees first.',
    summary:
      'Continuous local visibility: Google Business Profile and NAP foundations, geo-targeted social content, local on-site depth and paid reach.',
    image: '/images/offers/brand-awareness-dark-mode.png',
    imageAlt: 'Brand awareness megaphone and engagement illustration',
  },
  {
    id: 'lead-generation',
    slug: 'lead-generation',
    name: 'Lead Generation',
    targetQuery: 'lead generation services',
    headline: 'Scale acquisition against the numbers you already know.',
    summary:
      'Paid media in test-and-scale cycles, with organic creative testing alongside, for teams that know their CAC, LTV and ROAS.',
    image: '/images/offers/lead-generation-dark-mode.png',
    imageAlt: 'Lead generation funnel illustration',
  },
]

export function getGrowthSystem(id: GrowthSystemId): GrowthSystem {
  const system = GROWTH_SYSTEMS.find((s) => s.id === id)
  // Unreachable while `GrowthSystemId` and the array agree; verify:registry
  // asserts they do.
  if (!system) throw new Error(`Unknown growth system: ${id}`)
  return system
}

/** Site-relative path to a growth system's page. */
export function growthSystemPath(id: GrowthSystemId): string {
  return `${GROWTH_SYSTEMS_BASE}/${getGrowthSystem(id).slug}`
}
